import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import xlsx, { IJsonSheet } from 'json-as-xlsx';
import { Bar } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';

import DateRangeSelector from '../../../components/DateRangeSelector';
import {
  BillingDashboard as BillingDashboardData,
  BillingTotals,
  Granularity,
  OrdersAmount,
  share,
  variation,
} from '../models/BillingDashboardModel';
import { fetchBillingDashboard } from '../services/BillingDashboardCRUD';

// Metronic's primary and success colours.
const SUBSCRIPTION_COLOR = '#009ef7';
// The same blue, faded: pending subscription revenue has not been charged yet.
const SUBSCRIPTION_PENDING_COLOR = 'rgba(0, 158, 247, 0.35)';
const B2B_COLOR = '#50cd89';
const TOTAL_COLOR = '#181c32';
const CHART_HEIGHT_PX = 360;
const GRANULARITY_SELECT_WIDTH_PX = 170;
const EXCEL_EUROS_FORMAT = '#,##0.00 €';
const GRANULARITY_LABEL_KEYS: Record<Granularity, string> = {
  day: 'billingDashboard.granularityDay',
  week: 'billingDashboard.granularityWeek',
  month: 'billingDashboard.granularityMonth',
};

const euros = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
});
const compactEuros = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const signedPercent = new Intl.NumberFormat('es-ES', {
  style: 'percent',
  maximumFractionDigits: 1,
  signDisplay: 'exceptZero',
});
const percent = new Intl.NumberFormat('es-ES', {
  style: 'percent',
  maximumFractionDigits: 1,
});
const integer = new Intl.NumberFormat('es-ES');
const monthLabel = new Intl.DateTimeFormat('es-ES', {
  month: 'short',
  year: 'numeric',
});
const countryNames = new Intl.DisplayNames(['es'], { type: 'region' });

// DRF answers a 400 as {field: message}: show the first message, which is
// already translated, and fall back to a generic one for anything else.
function apiErrorMessage(error: any, fallback: string): string {
  const data = error?.response?.data;
  if (data && typeof data === 'object') {
    const first = Object.values(data)[0];
    const message = Array.isArray(first) ? first[0] : first;
    if (typeof message === 'string') return message;
  }
  return fallback;
}

function formatDay(isoDate: string): string {
  return moment(isoDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
}

interface KpiCardProps {
  title: string;
  color: string;
  amount: number;
  // Null when the range is not made of whole months: nothing to compare with.
  previousAmount: number | null;
  details: { label: string; value: string }[];
  hint?: string;
}

function Comparison({
  amount,
  previousAmount,
}: {
  amount: number;
  previousAmount: number;
}) {
  const intl = useIntl();
  const change = variation(amount, previousAmount);

  return (
    <div className="mb-5 fs-7">
      {change === null ? (
        <span className="text-muted">
          {intl.formatMessage({ id: 'billingDashboard.noComparison' })}
        </span>
      ) : (
        <span
          className={`badge badge-light-${
            change >= 0 ? 'success' : 'danger'
          } me-2`}
        >
          {signedPercent.format(change)}
        </span>
      )}
      <span className="text-muted">
        {intl.formatMessage(
          { id: 'billingDashboard.vsPrevious' },
          { amount: euros.format(previousAmount) }
        )}
      </span>
    </div>
  );
}

function KpiCard({
  title,
  color,
  amount,
  previousAmount,
  details,
  hint,
}: KpiCardProps) {
  return (
    <div className="card h-100 border border-gray-300">
      <div className="card-body p-6 d-flex flex-column">
        <div className="d-flex align-items-center mb-3">
          <span
            className="rounded-circle me-2"
            style={{ width: 10, height: 10, backgroundColor: color }}
          />
          <span className="fs-5 fw-bolder text-gray-800">{title}</span>
        </div>
        <div
          className={`fs-2hx fw-bolder text-dark lh-1 ${
            previousAmount === null ? 'mb-5' : 'mb-3'
          }`}
        >
          {euros.format(amount)}
        </div>
        {previousAmount !== null && (
          <Comparison amount={amount} previousAmount={previousAmount} />
        )}
        {details.map(({ label, value }) => (
          <div key={label} className="d-flex justify-content-between fs-6 mb-1">
            <span className="text-muted">{label}</span>
            <span className="fw-bold text-gray-800">{value}</span>
          </div>
        ))}
        {hint && <div className="text-muted fs-8 mt-auto pt-4">{hint}</div>}
      </div>
    </div>
  );
}

export default function BillingDashboard() {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id }, values);

  // Ranges run to the end of their period, so pending revenue shows up next to
  // what has already been charged.
  const [since, setSince] = useState<Date>(moment().startOf('month').toDate());
  const [until, setUntil] = useState<Date>(moment().endOf('month').toDate());
  // Empty lets the backend pick one from the length of the range.
  const [granularity, setGranularity] = useState<Granularity | ''>('');
  // Bumped by the refresh button to read the same range again.
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const [dashboard, setDashboard] = useState<BillingDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // A newer request (or leaving the page) cancels this one, so a slow answer
    // never overwrites a fresher one.
    const source = axios.CancelToken.source();
    let isCurrent = true;
    setLoading(true);
    fetchBillingDashboard(
      { since, until, granularity: granularity || undefined },
      source.token
    )
      .then(({ data }) => {
        if (isCurrent) setDashboard(data);
      })
      .catch((error) => {
        if (!isCurrent || axios.isCancel(error)) return;
        console.warn(error);
        toast.error(
          apiErrorMessage(
            error,
            intl.formatMessage({ id: 'billingDashboard.fetchError' })
          )
        );
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });
    return () => {
      isCurrent = false;
      source.cancel();
    };
  }, [since, until, granularity, refreshCount, intl]);

  const quickRanges = [
    {
      label: t('billingDashboard.today'),
      since: moment().startOf('day').toDate(),
      until: moment().toDate(),
    },
    {
      label: t('billingDashboard.thisWeek'),
      since: moment().startOf('isoWeek').toDate(),
      until: moment().endOf('isoWeek').toDate(),
    },
    {
      label: t('billingDashboard.thisMonth'),
      since: moment().startOf('month').toDate(),
      until: moment().endOf('month').toDate(),
    },
    {
      label: t('billingDashboard.previousMonth'),
      since: moment().subtract(1, 'month').startOf('month').toDate(),
      until: moment().subtract(1, 'month').endOf('month').toDate(),
    },
    {
      label: t('billingDashboard.thisYear'),
      since: moment().startOf('year').toDate(),
      until: moment().endOf('year').toDate(),
    },
  ];

  function periodLabel(period: string, periodGranularity: Granularity) {
    const date = moment(period, 'YYYY-MM-DD');
    if (periodGranularity === 'month') return monthLabel.format(date.toDate());
    if (periodGranularity === 'week') {
      return t('billingDashboard.weekOf', { date: date.format('DD/MM') });
    }
    return date.format('DD/MM');
  }

  function countryName(code: string | null): string {
    return code
      ? (countryNames.of(code) ?? code)
      : t('billingDashboard.unknownCountry');
  }

  function exportToExcel() {
    if (!dashboard) return;
    const { totals, previous_period: previous } = dashboard;
    const eurosColumn = (label: string, value: string) => ({
      label,
      value,
      format: EXCEL_EUROS_FORMAT,
    });
    // Each row of the summary and how to read its figures out of some totals.
    const channels: [string, (of: BillingTotals) => OrdersAmount][] = [
      [t('billingDashboard.total'), (of) => of.total],
      [t('billingDashboard.subscription'), (of) => of.subscription],
      [
        t('billingDashboard.subscriptionCharged'),
        (of) => of.subscription.charged,
      ],
      [
        t('billingDashboard.subscriptionPending'),
        (of) => of.subscription.pending,
      ],
      [t('billingDashboard.b2b'), (of) => of.b2b],
      [
        t('billingDashboard.b2bCreditNotes'),
        (of) => ({
          amount: of.b2b.credit_notes.amount,
          orders: of.b2b.credit_notes.count,
        }),
      ],
    ];
    const currentAmountColumn = eurosColumn(
      `${t('billingDashboard.currentPeriod')} (${formatDay(
        dashboard.since
      )} - ${formatDay(dashboard.until)})`,
      'amount'
    );
    const ordersColumn = {
      label: t('billingDashboard.ordersAndInvoices'),
      value: 'orders',
    };

    // Without whole months there is no previous period to set them against.
    const summarySheet: IJsonSheet = previous
      ? {
          sheet: t('billingDashboard.summarySheet'),
          columns: [
            { label: t('billingDashboard.channel'), value: 'channel' },
            currentAmountColumn,
            eurosColumn(
              `${t('billingDashboard.previousPeriod')} (${formatDay(
                previous.since
              )} - ${formatDay(previous.until)})`,
              'previousAmount'
            ),
            { label: t('billingDashboard.variation'), value: 'variation' },
            ordersColumn,
            {
              label: `${t('billingDashboard.ordersAndInvoices')} (${t(
                'billingDashboard.previousPeriod'
              )})`,
              value: 'previousOrders',
            },
          ],
          content: channels.map(([channel, figures]) => {
            const current = figures(totals);
            const before = figures(previous.totals);
            const change = variation(current.amount, before.amount);
            return {
              channel,
              amount: current.amount,
              previousAmount: before.amount,
              variation: change === null ? '' : signedPercent.format(change),
              orders: current.orders,
              previousOrders: before.orders,
            };
          }),
        }
      : {
          sheet: t('billingDashboard.summarySheet'),
          columns: [
            { label: t('billingDashboard.channel'), value: 'channel' },
            currentAmountColumn,
            ordersColumn,
          ],
          content: channels.map(([channel, figures]) => ({
            channel,
            ...figures(totals),
          })),
        };
    const amountColumns = [
      eurosColumn(
        t('billingDashboard.subscriptionCharged'),
        'subscription_charged'
      ),
      eurosColumn(
        t('billingDashboard.subscriptionPending'),
        'subscription_pending'
      ),
      eurosColumn(t('billingDashboard.subscription'), 'subscription'),
      eurosColumn(t('billingDashboard.b2b'), 'b2b'),
      eurosColumn(t('billingDashboard.total'), 'total'),
    ];
    const evolutionSheet: IJsonSheet = {
      sheet: t('billingDashboard.evolution'),
      columns: [
        { label: t('billingDashboard.period'), value: 'period' },
        ...amountColumns,
      ],
      content: dashboard.series.map((point) => ({
        ...point,
        period: periodLabel(point.period, dashboard.granularity),
      })),
    };
    const countriesSheet: IJsonSheet = {
      sheet: t('billingDashboard.byCountry'),
      columns: [
        { label: t('billingDashboard.country'), value: 'country' },
        ...amountColumns,
      ],
      content: dashboard.by_country.map((row) => ({
        ...row,
        country: countryName(row.country),
      })),
    };

    xlsx([summarySheet, evolutionSheet, countriesSheet], {
      fileName: `facturacion-${dashboard.since}-${dashboard.until}`,
      extraLength: 0,
      writeMode: 'writeFile',
      writeOptions: {},
    });
  }

  function renderChart(data: BillingDashboardData) {
    const chartData: ChartData<'bar'> = {
      labels: data.series.map((point) =>
        periodLabel(point.period, data.granularity)
      ),
      datasets: [
        {
          label: t('billingDashboard.subscriptionCharged'),
          data: data.series.map((point) => point.subscription_charged),
          backgroundColor: SUBSCRIPTION_COLOR,
        },
        {
          label: t('billingDashboard.subscriptionPending'),
          data: data.series.map((point) => point.subscription_pending),
          backgroundColor: SUBSCRIPTION_PENDING_COLOR,
        },
        {
          label: t('billingDashboard.b2b'),
          data: data.series.map((point) => point.b2b),
          backgroundColor: B2B_COLOR,
        },
      ],
    };
    const options: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: { stacked: true, grid: { display: false } },
        y: {
          stacked: true,
          ticks: { callback: (value) => compactEuros.format(Number(value)) },
        },
      },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (item) =>
              `${item.dataset.label}: ${euros.format(item.parsed.y)}`,
            footer: (items) =>
              `${t('billingDashboard.total')}: ${euros.format(
                data.series[items[0].dataIndex].total
              )}`,
          },
        },
      },
    };

    return (
      <div style={{ height: `${CHART_HEIGHT_PX}px` }}>
        <Bar data={chartData} options={options} />
      </div>
    );
  }

  function renderCountries(data: BillingDashboardData) {
    const grandTotal = data.totals.total.amount;
    const formatShare = (amount: number) => {
      const value = share(amount, grandTotal);
      return value === null ? '-' : percent.format(value);
    };

    return (
      <div className="table-responsive">
        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3">
          <thead>
            <tr className="fw-bolder text-muted">
              <th>{t('billingDashboard.country')}</th>
              <th className="text-end">
                {t('billingDashboard.subscriptionCharged')}
              </th>
              <th className="text-end">
                {t('billingDashboard.subscriptionPending')}
              </th>
              <th className="text-end">{t('billingDashboard.b2b')}</th>
              <th className="text-end">{t('billingDashboard.total')}</th>
              <th className="text-end">{t('billingDashboard.share')}</th>
            </tr>
          </thead>
          <tbody>
            {data.by_country.map((row) => (
              <tr key={row.country ?? 'unknown'}>
                <td className="fw-bold">{countryName(row.country)}</td>
                <td className="text-end">
                  {euros.format(row.subscription_charged)}
                </td>
                <td className="text-end">
                  {euros.format(row.subscription_pending)}
                </td>
                <td className="text-end">{euros.format(row.b2b)}</td>
                <td className="text-end fw-bold">{euros.format(row.total)}</td>
                <td className="text-end">{formatShare(row.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="fw-bolder border-top border-gray-300">
              <td>{t('billingDashboard.total')}</td>
              <td className="text-end">
                {euros.format(data.totals.subscription.charged.amount)}
              </td>
              <td className="text-end">
                {euros.format(data.totals.subscription.pending.amount)}
              </td>
              <td className="text-end">
                {euros.format(data.totals.b2b.amount)}
              </td>
              <td className="text-end">{euros.format(grandTotal)}</td>
              <td className="text-end">{formatShare(grandTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  function renderDashboard(data: BillingDashboardData) {
    const { totals, previous_period: previous } = data;
    const hasData =
      totals.total.orders > 0 || totals.b2b.credit_notes.count > 0;
    const amountWithOrders = ({ amount, orders }: OrdersAmount) =>
      t('billingDashboard.amountWithOrders', {
        amount: euros.format(amount),
        orders: integer.format(orders),
      });

    return (
      <>
        <div className="row g-5 mb-8">
          <div className="col-lg-4">
            <KpiCard
              title={t('billingDashboard.total')}
              color={TOTAL_COLOR}
              amount={totals.total.amount}
              previousAmount={previous?.totals.total.amount ?? null}
              details={[
                {
                  label: t('billingDashboard.ordersAndInvoices'),
                  value: integer.format(totals.total.orders),
                },
                {
                  label: t('billingDashboard.pending'),
                  value: euros.format(totals.subscription.pending.amount),
                },
              ]}
              hint={t('billingDashboard.totalHint')}
            />
          </div>
          <div className="col-lg-4">
            <KpiCard
              title={t('billingDashboard.subscription')}
              color={SUBSCRIPTION_COLOR}
              amount={totals.subscription.amount}
              previousAmount={previous?.totals.subscription.amount ?? null}
              details={[
                {
                  label: t('billingDashboard.orders'),
                  value: integer.format(totals.subscription.orders),
                },
                {
                  label: t('billingDashboard.charged'),
                  value: amountWithOrders(totals.subscription.charged),
                },
                {
                  label: t('billingDashboard.pending'),
                  value: amountWithOrders(totals.subscription.pending),
                },
                {
                  label: t('billingDashboard.avgTicket'),
                  value: euros.format(totals.subscription.avg_ticket),
                },
              ]}
              hint={t('billingDashboard.subscriptionHint')}
            />
          </div>
          <div className="col-lg-4">
            <KpiCard
              title={t('billingDashboard.b2b')}
              color={B2B_COLOR}
              amount={totals.b2b.amount}
              previousAmount={previous?.totals.b2b.amount ?? null}
              details={[
                {
                  label: t('billingDashboard.invoices'),
                  value: integer.format(totals.b2b.orders),
                },
                {
                  label: t('billingDashboard.avgTicket'),
                  value: euros.format(totals.b2b.avg_ticket),
                },
                {
                  label: t('billingDashboard.creditNotes'),
                  value: `${integer.format(
                    totals.b2b.credit_notes.count
                  )} (${euros.format(totals.b2b.credit_notes.amount)})`,
                },
              ]}
              hint={t('billingDashboard.b2bHint')}
            />
          </div>
        </div>

        {hasData ? (
          <>
            <h3 className="fs-4 fw-bolder mb-4">
              {t('billingDashboard.evolution')}
            </h3>
            <div className="mb-10">{renderChart(data)}</div>
            <h3 className="fs-4 fw-bolder mb-4">
              {t('billingDashboard.byCountry')}
            </h3>
            {renderCountries(data)}
          </>
        ) : (
          <div className="text-muted text-center py-10">
            {t('billingDashboard.noData')}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="card shadow-sm p-10">
      <div className="d-flex flex-wrap align-items-end gap-6 mb-4">
        <DateRangeSelector
          initialSince={since}
          initialUntil={until}
          quickRanges={quickRanges}
          onSelected={(newSince, newUntil) => {
            setSince(newSince);
            setUntil(newUntil);
          }}
          label={t('billingDashboard.dateRange')}
        />
        <div style={{ width: `${GRANULARITY_SELECT_WIDTH_PX}px` }}>
          <label className="form-label">
            {t('billingDashboard.granularity')}
          </label>
          <select
            className="form-select"
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as Granularity | '')}
          >
            <option value="">{t('billingDashboard.granularityAuto')}</option>
            {Object.entries(GRANULARITY_LABEL_KEYS).map(([value, labelKey]) => (
              <option key={value} value={value}>
                {t(labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div className="ms-auto d-flex flex-wrap align-items-center gap-3">
          {dashboard && (
            <span className="text-muted fs-7">
              {t('billingDashboard.updatedAt', {
                time: moment(dashboard.generated_at).format('HH:mm:ss'),
              })}
            </span>
          )}
          <button
            type="button"
            className="btn btn-light-primary"
            onClick={() => setRefreshCount((count) => count + 1)}
            disabled={loading}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : (
              <span className="fas fa-sync-alt me-2" />
            )}
            {t('billingDashboard.refresh')}
          </button>
          <button
            type="button"
            className="btn btn-light-success"
            onClick={exportToExcel}
            disabled={!dashboard || loading}
          >
            <span className="fas fa-file-excel me-2" />
            {t('billingDashboard.exportExcel')}
          </button>
        </div>
      </div>

      <div className="text-muted fs-7 mb-8">
        {t('billingDashboard.taxExcluded')}
        {dashboard?.previous_period &&
          ` · ${t('billingDashboard.comparedWith', {
            since: formatDay(dashboard.previous_period.since),
            until: formatDay(dashboard.previous_period.until),
          })}`}
      </div>

      {dashboard ? (
        <div style={{ opacity: loading ? 0.5 : 1 }}>
          {renderDashboard(dashboard)}
        </div>
      ) : (
        <div className="text-muted text-center py-10">
          {loading ? t('billingDashboard.loading') : ''}
        </div>
      )}
    </div>
  );
}
